import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import { useAuth } from '../contexts/AuthContext'

function MySubmissions() {
  const { user } = useAuth()
  const navigate = useNavigate()
  
  const [loading, setLoading] = useState(true)
  const [activeFilter, setActiveFilter] = useState('all')
  
  const [brandSubmissions, setBrandSubmissions] = useState([])
  const [lureSubmissions, setLureSubmissions] = useState([])
  const [variantSubmissions, setVariantSubmissions] = useState([])
  
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0
  })

  useEffect(() => {
    if (!user) {
      navigate('/login')
      return
    }
    fetchSubmissions()
  }, [user, navigate])

  const fetchSubmissions = async () => {
    try {
      // Fetch brand submissions
      const { data: brands } = await supabase
        .from('brand_submissions')
        .select('*')
        .eq('submitted_by', user.id)
        .order('created_at', { ascending: false })

      // Fetch lure submissions with brand info
      const { data: lures } = await supabase
        .from('lure_submissions')
        .select(`
          *,
          brands (brand_name),
          lure_types (type_name)
        `)
        .eq('submitted_by', user.id)
        .order('created_at', { ascending: false })

      // Fetch variant submissions with lure info
      const { data: variants } = await supabase
        .from('variant_submissions')
        .select(`
          *,
          base_lures (name, brands (brand_name))
        `)
        .eq('submitted_by', user.id)
        .order('created_at', { ascending: false })

      setBrandSubmissions(brands || [])
      setLureSubmissions(lures || [])
      setVariantSubmissions(variants || [])

      // Calculate stats
      const allSubmissions = [
        ...(brands || []),
        ...(lures || []),
        ...(variants || [])
      ]

      setStats({
        total: allSubmissions.length,
        pending: allSubmissions.filter(s => s.submission_status === 'pending').length,
        approved: allSubmissions.filter(s => s.submission_status === 'approved').length,
        rejected: allSubmissions.filter(s => s.submission_status === 'rejected').length
      })

      setLoading(false)
    } catch (error) {
      console.error('Error fetching submissions:', error)
      setLoading(false)
    }
  }

  const getStatusBadge = (status) => {
    const badges = {
      pending: { text: 'Pending Review', className: 'status-pending' },
      approved: { text: 'Approved ✓', className: 'status-approved' },
      rejected: { text: 'Rejected', className: 'status-rejected' }
    }
    return badges[status] || badges.pending
  }

  const filterSubmissions = (submissions) => {
    if (activeFilter === 'all') return submissions
    return submissions.filter(s => s.submission_status === activeFilter)
  }

  if (loading) {
    return <div className="loading">Loading your submissions...</div>
  }

  const allSubmissions = [
    ...brandSubmissions.map(s => ({ ...s, type: 'brand' })),
    ...lureSubmissions.map(s => ({ ...s, type: 'lure' })),
    ...variantSubmissions.map(s => ({ ...s, type: 'variant' }))
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))

  const filteredSubmissions = filterSubmissions(allSubmissions)

  return (
    <div className="my-submissions-page">
      <div className="page-header">
        <h1>My Submissions</h1>
        <p className="page-subtitle">
          Track the status of your database contributions
        </p>
        <Link to="/submit-content" className="btn-primary">
          + Submit New Content
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="submission-stats-grid">
        <div className="submission-stat-card">
          <div className="stat-number">{stats.total}</div>
          <div className="stat-label">Total Submissions</div>
        </div>
        <div className="submission-stat-card pending">
          <div className="stat-number">{stats.pending}</div>
          <div className="stat-label">Pending Review</div>
        </div>
        <div className="submission-stat-card approved">
          <div className="stat-number">{stats.approved}</div>
          <div className="stat-label">Approved</div>
        </div>
        <div className="submission-stat-card rejected">
          <div className="stat-number">{stats.rejected}</div>
          <div className="stat-label">Rejected</div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="submission-filter-tabs">
        <button
          onClick={() => setActiveFilter('all')}
          className={`filter-tab ${activeFilter === 'all' ? 'active' : ''}`}
        >
          All ({stats.total})
        </button>
        <button
          onClick={() => setActiveFilter('pending')}
          className={`filter-tab ${activeFilter === 'pending' ? 'active' : ''}`}
        >
          Pending ({stats.pending})
        </button>
        <button
          onClick={() => setActiveFilter('approved')}
          className={`filter-tab ${activeFilter === 'approved' ? 'active' : ''}`}
        >
          Approved ({stats.approved})
        </button>
        <button
          onClick={() => setActiveFilter('rejected')}
          className={`filter-tab ${activeFilter === 'rejected' ? 'active' : ''}`}
        >
          Rejected ({stats.rejected})
        </button>
      </div>

      {/* Submissions List */}
      {filteredSubmissions.length === 0 ? (
        <div className="no-submissions">
          <h3>No submissions found</h3>
          <p>
            {activeFilter === 'all' 
              ? "You haven't submitted any content yet."
              : `You have no ${activeFilter} submissions.`}
          </p>
          <Link to="/submit-content" className="btn-primary">
            Submit Your First Contribution
          </Link>
        </div>
      ) : (
        <div className="submissions-list">
          {filteredSubmissions.map((submission) => {
            const statusBadge = getStatusBadge(submission.submission_status)
            
            return (
              <div key={`${submission.type}-${submission.submission_id}`} className="submission-card">
                <div className="submission-card-header">
                  <div className="submission-type-badge">
                    {submission.type === 'brand' && '🏢 Brand'}
                    {submission.type === 'lure' && '🎣 Lure'}
                    {submission.type === 'variant' && '✨ Color Variant'}
                  </div>
                  <div className={`submission-status-badge ${statusBadge.className}`}>
                    {statusBadge.text}
                  </div>
                </div>

                <div className="submission-card-body">
                  <h3 className="submission-title">
                    {submission.type === 'brand' && submission.brand_name}
                    {submission.type === 'lure' && submission.lure_name}
                    {submission.type === 'variant' && (
                      submission.variant_name || submission.color_name
                    )}
                  </h3>

                  {submission.type === 'lure' && (
                    <p className="submission-details">
                      <strong>Brand:</strong> {submission.brands?.brand_name} | 
                      <strong> Type:</strong> {submission.lure_types?.type_name}
                    </p>
                  )}

                  {submission.type === 'variant' && (
                    <p className="submission-details">
                      <strong>Lure:</strong> {submission.base_lures?.name} ({submission.base_lures?.brands?.brand_name}) |
                      <strong> Color:</strong> {submission.color_name}
                    </p>
                  )}

                  {submission.type === 'brand' && submission.country && (
                    <p className="submission-details">
                      <strong>Country:</strong> {submission.country}
                    </p>
                  )}

                  {submission.description && (
                    <p className="submission-description">
                      {submission.description}
                    </p>
                  )}

                  {submission.admin_notes && (
                    <div className="admin-feedback">
                      <strong>Admin Feedback:</strong>
                      <p>{submission.admin_notes}</p>
                    </div>
                  )}

                  {submission.submission_status === 'approved' && (
                    <div className="approved-link">
                      {submission.type === 'lure' && submission.approved_lure_id && (
                        <Link to={`/lure/${submission.approved_lure_id}`}>
                          View in Database →
                        </Link>
                      )}
                      {submission.type === 'brand' && submission.approved_brand_id && (
                        <span className="success-text">✓ Added to database</span>
                      )}
                      {submission.type === 'variant' && submission.approved_variant_id && (
                        <Link to={`/lure/${submission.base_lures?.base_lure_id}`}>
                          View Lure →
                        </Link>
                      )}
                    </div>
                  )}
                </div>

                <div className="submission-card-footer">
                  <span className="submission-date">
                    Submitted {new Date(submission.created_at).toLocaleDateString()}
                  </span>
                  {submission.reviewed_at && (
                    <span className="review-date">
                      Reviewed {new Date(submission.reviewed_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default MySubmissions
